import React from "react";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

export const consolidationStatusLabels = (catalog = []) => Object.fromEntries(
  catalog.map(item => [item.canonical_status || item.code, item.label_nl]));

const BLOCK_REASONS = {
  confirmed_event_required: "Verplichte operationele bevestiging ontbreekt.",
  estimate_requires_confirmation: "Verplichte operationele bevestiging ontbreekt.",
  protected_release_required: "Operationele vrijgave ontbreekt.",
  arrival_confirmation_required: "Aankomst is nog niet operationeel bevestigd.",
  milestone_manual_only: "Deze milestone staat op handmatige verwerking.",
  milestone_time_missing: "Tijdstip ontbreekt; alleen een datum is niet voldoende.",
  shipment_sync_incomplete: "Niet alle gekoppelde zendingen konden worden bijgewerkt.",
  automation_paused: "Automatische verwerking is gepauzeerd.",
};

export const MilestoneExecutionNotice = ({ consolidation, catalog = [] }) => {
  const reports = consolidation?.milestone_execution_report || {};
  return <div className="space-y-2 text-xs" data-testid="milestone-execution-report">
    {consolidation?.automation_paused && <p data-testid="milestone-automation-paused" className="text-amber-800">
      Automatische verwerking is gepauzeerd. {consolidation.tracking_notice?.reason || ""}
    </p>}
    {catalog.map(m => {
      const r = reports[m.key];
      if (!r) return null;
      const planned = consolidation?.milestones?.[m.key]?.planned_at || consolidation?.[m.consolidation_field];
      if (r.planned_at && planned && new Date(r.planned_at).getTime() !== new Date(planned).getTime()) return null;
      return <div key={m.key} className="border-b py-2 break-words" data-testid={`milestone-execution-${m.key}`}>
        <strong>{m.label_nl}</strong> · {r.result === "deferred" ? "Open — geblokkeerd" : ["applied", "idempotent"].includes(r.result) ? "Verwerkt" : "Niet verwerkt"}
        {r.skipped_reason && <p data-testid={`milestone-blocker-${m.key}`}>{BLOCK_REASONS[r.skipped_reason] || r.skipped_reason}</p>}
      </div>;
    })}
  </div>;
};

export const ConsolidationSyncNotice = ({ report, prefix = "consolidation-sync" }) => {
  if (!report) return null;
  const complete = report.complete === true;
  const Icon = complete ? CheckCircle2 : AlertTriangle;
  return <section role="status" aria-live="polite" data-testid={`${prefix}-result`}
    className={`border-l-4 p-3 min-w-0 ${complete ? "border-emerald-600 bg-emerald-50" : "border-amber-500 bg-amber-50"}`}>
    <div className="flex items-start gap-2">
      <Icon className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold" data-testid={`${prefix}-status`}>
          {complete ? "Synchronisatie voltooid" : "Update onvolledig"}
        </p>
        <p className="text-xs mt-1" data-testid={`${prefix}-counts`}>
          {(report.updated || 0) + (report.unchanged || 0)} van {report.total || 0} zendingen op de bijbehorende status
        </p>
        {report.failure_scope === "consolidation" && <p className="text-xs mt-1" data-testid={`${prefix}-parent-error`}>
          Consolidatiefout: verwerking van {report.affected ?? report.total ?? 0} zendingen niet gestart.
        </p>}
        {(report.problems || []).map((reason, i) => <p key={i} className="text-xs mt-1 break-words"
          data-testid={`${prefix}-problem-${i}`}>{reason === "consolidation_not_found"
            ? "Consolidatie niet gevonden binnen de bevoegde context." : reason}</p>)}
        <ul className="mt-2 divide-y divide-slate-200">
          {(report.per_shipment || []).map(row => <li key={row.shipment_id}
            className="py-2 flex flex-col sm:flex-row sm:gap-4 min-w-0 text-xs" data-testid={`${prefix}-shipment-${row.shipment_id}`}>
            <span className="font-semibold break-all sm:w-48 sm:shrink-0" data-testid={`${prefix}-number-${row.shipment_id}`}>
              {row.w_number || "Zending niet toegankelijk"}
            </span>
            <span className="break-words min-w-0 flex-1 [overflow-wrap:anywhere]" data-testid={`${prefix}-reason-${row.shipment_id}`}>
              {report.failure_scope === "consolidation" ? "Niet verwerkt door consolidatiefout." : row.result === "applied" ? "Bijgewerkt" : ["idempotent", "noop"].includes(row.result)
                ? "Reeds bijgewerkt" : row.skipped_reason || "Status niet bijgewerkt"}
              {row.result === "blocked_by_hold" && row.release_reason && <span
                className="block mt-1 whitespace-pre-wrap [overflow-wrap:anywhere]"
                data-testid={`${prefix}-hold-reason-${row.shipment_id}`}>{row.release_reason}</span>}
            </span>
          </li>)}
        </ul>
      </div>
    </div>
  </section>;
};